import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { Card, CardContent } from 'components/ui/card';
import { questionTextToPlainText, RevealCardText } from 'utils/questionMarkdown';
import { Icon } from './icons/Icon';

interface RevealCardProps {
  frontText: string;
  backText: string;
  onReveal: (optionId: number) => void;
  isRevealed: boolean;
  optionId: number;
  /** True once the learner has opened this card at least once. */
  hasBeenRevealed?: boolean;
}

const FLIP_ANIMATION_DURATION_MS = 450;

export const RevealCard: React.FC<RevealCardProps> = ({ frontText, backText, onReveal, isRevealed, optionId, hasBeenRevealed = false }) => {
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const flipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generatedId = useId().replace(/:/g, '');
  const revealedContentId = `reveal-card-content-${generatedId}`;
  const accessibleName = questionTextToPlainText(frontText, { literalCitations: true }).replace(/\s+/g, ' ').trim() || 'Reveal card';

  useLayoutEffect(() => {
    let frame: number | undefined;
    const measureHeight = () => {
      if (!containerRef.current) return;

      // Temporarily make both cards visible to measure
      const container = containerRef.current;
      const frontCard = container.querySelector('.flip-card-front') as HTMLElement;
      const backCard = container.querySelector('.flip-card-back') as HTMLElement;

      if (!frontCard || !backCard) return;

      // Use requestAnimationFrame to batch DOM reads
      if (frame !== undefined) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = undefined;
        // Store original styles
        const frontOriginalPosition = frontCard.style.position;
        const backOriginalPosition = backCard.style.position;
        const backOriginalTransform = backCard.style.transform;

        // Temporarily reset positioning to measure natural height
        frontCard.style.position = 'static';
        backCard.style.position = 'static';
        backCard.style.transform = 'none';

        const frontHeight = frontCard.offsetHeight;
        const backHeight = backCard.offsetHeight;
        const maxHeight = Math.max(frontHeight, backHeight);

        // Restore original styles
        frontCard.style.position = frontOriginalPosition;
        backCard.style.position = backOriginalPosition;
        backCard.style.transform = backOriginalTransform;

        setContainerHeight(maxHeight);
      });
    };

    // Measure after a short delay to ensure content is rendered
    const timer = setTimeout(measureHeight, 50);

    // Card width can change without a window resize (for example beside a panel).
    let lastWidth = containerRef.current?.getBoundingClientRect().width;
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(([entry]) => {
      if (entry && entry.contentRect.width !== lastWidth) {
        lastWidth = entry.contentRect.width;
        measureHeight();
      }
    });
    if (containerRef.current) observer?.observe(containerRef.current);

    window.addEventListener('resize', measureHeight);
    return () => {
      clearTimeout(timer);
      if (frame !== undefined) cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', measureHeight);
    };
  }, [frontText, backText]);

  useEffect(() => () => {
    if (flipTimerRef.current) {
      clearTimeout(flipTimerRef.current);
    }
  }, []);

  const handleFlip = () => {
    if (isFlipping) return; // Prevent multiple flips at once

    const prefersReducedMotion =
      typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReducedMotion) {
      setIsFlipping(true);
    }
    onReveal(optionId);

    if (prefersReducedMotion) {
      return;
    }

    flipTimerRef.current = setTimeout(() => {
      setIsFlipping(false);
      flipTimerRef.current = null;
    }, FLIP_ANIMATION_DURATION_MS);
  };

  if (!frontText && !backText) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="flip-card-container perspective-1000 group relative"
      style={containerHeight > 0 ? { height: `${containerHeight}px` } : undefined}
    >
      <button
        type="button"
        className="absolute inset-0 z-20 h-full w-full cursor-pointer rounded-3xl border-0 bg-transparent p-0 text-inherit focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-foundation-orange-500"
        onClick={handleFlip}
        aria-label={accessibleName}
        aria-expanded={isRevealed}
        aria-controls={revealedContentId}
      />
      <div
        className={`flip-card-inner w-full h-full transform-style-3d ${
          isRevealed ? "rotate-y-180" : ""
        }`}
      >
        {/* Front Side */}
        <Card 
          maxWidth='none'
          variant='white' 
          disableAnimation
          data-testid={`reveal-card-${optionId}-front`}
          aria-hidden="true"
          className="flip-card-face flip-card-front pointer-events-none absolute inset-0 flex items-center justify-center transition-shadow group-hover:shadow-lg backface-hidden px-5 py-6 overflow-visible"
        >
          <CardContent className="flex min-w-0 w-full flex-col items-center justify-center gap-2 p-0 text-center">
            <Icon name='ArrowCircle' size={26}/>
            <h3 className="font-headings-h3 font-[number:var(--headings-h3-font-weight)] text-foundation-base-darkbase-dark-500 text-[length:var(--headings-h3-font-size)] tracking-[var(--headings-h3-letter-spacing)] leading-[var(--headings-h3-line-height)] [font-style:var(--headings-h3-font-style)] break-words whitespace-pre-wrap w-full">
              <RevealCardText text={frontText} face="front" />
            </h3>
            <span className="text-xs font-semibold uppercase tracking-[0.06em] text-foundation-base-darkbase-dark-300">
              {hasBeenRevealed ? 'Tap to see again' : 'Tap to reveal'}
            </span>
          </CardContent>
        </Card>

        {/* Back Side */}
        <Card 
          maxWidth='none'
          variant='white' 
          disableAnimation
          data-testid={`reveal-card-${optionId}-content`}
          id={revealedContentId}
          role="region"
          aria-label={`${accessibleName} revealed content`}
          aria-hidden={!isRevealed}
          className="flip-card-face flip-card-back pointer-events-none absolute inset-0 flex items-center justify-center transition-shadow group-hover:shadow-lg backface-hidden rotate-y-180 px-5 py-6 overflow-visible"
        >
          <CardContent className="flex min-w-0 w-full flex-col items-center justify-center gap-2 p-0 text-center">
            {frontText ? (
              <span
                data-testid={`reveal-card-${optionId}-back-title`}
                className="w-full break-words text-xs font-semibold uppercase tracking-[0.06em] text-foundation-orange-500"
              >
                <RevealCardText text={frontText} face="front" />
              </span>
            ) : null}
            <div className="type-body-m text-foundation-base-darkbase-dark-500 break-words whitespace-pre-wrap w-full">
              <RevealCardText text={backText} face="back" />
            </div>
          </CardContent>
        </Card>
      </div>
      {hasBeenRevealed ? (
        <span
          data-testid={`reveal-card-${optionId}-seen`}
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-3 z-10 grid h-5 w-5 place-items-center rounded-full bg-foundation-base-darkbase-dark-500 text-[10px] text-white"
        >
          ✓
        </span>
      ) : null}
    </div>
  );
};
